import React, { useState } from "react";
import { Wand2, Loader2, PlayCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ModelTypeSelect } from "@/components/ModelTypeSelect";
import { CardSourcePicker } from "@/components/CardSourcePicker";
import { UsageCost } from "@/components/UsageCost";
import { api } from "@/lib/api";
import { useShared, useSharedDraft, EditedBy, RunningBy, TypingMark, useRunWatcher } from "@/lib/sharedDraft";
import { WordingFeedback } from "@/components/WordingFeedback";

const ADDITION = "__addition";

function PlacementPicker({ card, placement, setPlacement, disabled }) {
  const names = card?.rule_names || (card?.rules || []).map((r) => r.name);
  return (
    <div className="flex items-center gap-2 flex-wrap text-sm" data-testid="draft-placement">
      <span className="text-muted-foreground">The new rule is</span>
      <Select value={placement.mode === "replace" ? "replace" : ADDITION} onValueChange={(v) => setPlacement({ mode: v === "replace" ? "replace" : "addition", replaces: placement.replaces || "" })} disabled={disabled}>
        <SelectTrigger className="w-[200px] h-8" data-testid="draft-placement-mode"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ADDITION} data-testid="draft-placement-addition">in addition to the card</SelectItem>
          <SelectItem value="replace" data-testid="draft-placement-replace">replacing a rule</SelectItem>
        </SelectContent>
      </Select>
      {placement.mode === "replace" && (names.length ? (
        <Select value={placement.replaces || ""} onValueChange={(v) => setPlacement({ ...placement, replaces: v })} disabled={disabled}>
          <SelectTrigger className="w-[240px] h-8" data-testid="draft-replaces-select"><SelectValue placeholder="Which rule?" /></SelectTrigger>
          <SelectContent>
            {names.map((n) => <SelectItem key={n} value={n} data-testid={`draft-replaces-option-${n}`}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : (
        <Input value={placement.replaces || ""} onChange={(e) => setPlacement({ ...placement, replaces: e.target.value })} placeholder="Name of the rule it replaces" className="h-8 w-[240px]" disabled={disabled} data-testid="draft-replaces-input" />
      ))}
    </div>
  );
}

export const placementText = (p) =>
  p?.mode === "replace" && p.replaces ? `this new rule REPLACES the card's existing rule "${p.replaces}"` : "this new rule is IN ADDITION to the card's existing rules";

function OptionCard({ option, index, onChange, onUse, ruleType, intent }) {
  return (
    <Card className="border-violet-800/40" data-testid={`draft-option-${index}`}>
      <CardContent className="p-4 space-y-2">
        {option.edited_by && <TypingMark name={option.edited_by} byId={option.edited_by_id} at={option.edited_at} testId={`draft-option-${index}-edited-by`} />}
        <Input value={option.name} onChange={(e) => onChange({ ...option, name: e.target.value })} className="h-8 font-semibold" data-testid={`draft-option-${index}-name`} />
        <Textarea value={option.text} onChange={(e) => onChange({ ...option, text: e.target.value })} className="min-h-[90px] text-sm" data-testid={`draft-option-${index}-text`} />
        {option.rationale && <p className="text-xs text-muted-foreground italic">{option.rationale}</p>}
        <WordingFeedback source="draft" text={option.text} ruleName={option.name} ruleType={ruleType} intent={intent} testId={`draft-option-${index}-feedback`} />
        <Button size="sm" onClick={onUse} disabled={!option.text.trim()} data-testid={`draft-option-${index}-use`}>
          <PlayCircle className="w-4 h-4 mr-1.5" /> Run model check with this
        </Button>
      </CardContent>
    </Card>
  );
}

/** Mode 3: playtester writes an intent, the LLM drafts 3 wordings to pick from. */
export function DraftComposer({ ruleTypes, onUse }) {
  const me = useSharedDraft()?.me;
  const [intent, setIntent] = useShared("draft_intent", "");
  const [ruleType, setRuleType] = useShared("draft_rule_type", "");
  const [modelTypes, setModelTypes] = useShared("draft_model_types", []);
  const [card, setCard] = useShared("draft_card", null);
  const [placement, setPlacement] = useShared("draft_placement", { mode: "addition", replaces: "" });
  const [options, setOptions] = useShared("draft_options", []);
  const [refine, setRefine] = useShared("draft_refine", "");
  const [usage, setUsage] = useShared("draft_usage", null);
  const [loading, setLoading] = useShared("draft_loading", false);
  const [error, setError] = useShared("draft_error", null);

  const [localBusy, setLocalBusy] = useState(false);
  useRunWatcher({ loading, localRunning: localBusy, noJobTimeoutMs: 120000, onStop: () => setLoading(false) });
  const cardPayload = card && { ...card, placement: placementText(placement) };
  const draft = async (feedback = "") => {
    setLocalBusy(true);
    setLoading(true);
    setError(null);
    try {
      const r = await api.draftRules({ intent, rule_type: ruleType || null, model_types: modelTypes, card: cardPayload, feedback: feedback || null });
      setOptions(r.options.map((o) => ({ ...o, id: crypto.randomUUID() })));
      setUsage(r.usage);
      setRefine("");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
      setLocalBusy(false);
    }
  };

  return (
    <div className="grid gap-4" data-testid="draft-composer">
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-violet-400" />
            <h2 className="text-lg font-semibold">Write a rule from intent</h2>
            <EditedBy field="draft_intent" />
          </div>
          <p className="text-sm text-muted-foreground">Describe what the rule should achieve. The LLM writes three wordings; edit one and run the full model check on it.</p>
          <Textarea value={intent} onChange={(e) => setIntent(e.target.value)} placeholder="e.g. Make this warcaster better at protecting his warjacks from enemy shooting, without making him harder to kill himself…" className="min-h-[100px]" disabled={loading} data-testid="draft-intent-input" />
          <div className="grid md:grid-cols-2 gap-2">
            <Select value={ruleType} onValueChange={setRuleType} disabled={loading}>
              <SelectTrigger data-testid="draft-rule-type-select"><SelectValue placeholder="Rule type (optional)…" /></SelectTrigger>
              <SelectContent>
                {ruleTypes.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`draft-rule-type-option-${t.id}`}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <ModelTypeSelect value={modelTypes} onChange={setModelTypes} disabled={loading} testId="draft-model-types" />
          </div>
          <CardSourcePicker card={card} onChange={setCard} disabled={loading} optional markNew={false} />
          {card && <PlacementPicker card={card} placement={placement} setPlacement={setPlacement} disabled={loading} />}
          <Button onClick={() => draft()} disabled={loading || !intent.trim()} data-testid="draft-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1.5" />}
            {options.length ? "Write new options" : "Write 3 options"}
          </Button>
          <RunningBy field="draft_loading" label="Writing options" />
          {error && <p className="text-sm text-rose-300" data-testid="draft-error">{error}</p>}
        </CardContent>
      </Card>

      {options.length > 0 && (
        <>
          <UsageCost usage={usage} label="Drafting" testId="draft-usage-cost" />
          <div className="grid lg:grid-cols-3 gap-3">
            {options.map((o, i) => (
              <OptionCard
                key={o.id || i}
                index={i}
                option={o}
                ruleType={ruleType}
                intent={intent}
                onChange={(next) => setOptions((os) => os.map((x, j) => (j === i ? { ...next, edited_by: me?.name, edited_by_id: me?.id, edited_at: new Date().toISOString() } : x)))}
                onUse={() => onUse({
                  card: cardPayload,
                  ruleType,
                  modelTypes,
                  rules: [{ name: o.name, proposed_text: o.text, intent, rule_type: ruleType }],
                })}
              />
            ))}
          </div>
          <div className="flex gap-2 items-start">
            <Textarea value={refine} onChange={(e) => setRefine(e.target.value)} placeholder="Not quite? Tell the LLM what to change (e.g. 'shorter, and only against ranged attacks')…" className="min-h-[60px] text-sm" disabled={loading} data-testid="draft-refine-input" />
            <Button variant="outline" onClick={() => draft(refine)} disabled={loading || !refine.trim()} data-testid="draft-refine-btn">Refine</Button>
          </div>
        </>
      )}
    </div>
  );
}
